import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Select, Dropdown, Form } from 'antd';
import { PlusOutlined, CloseOutlined, CodeOutlined } from '@ant-design/icons';
import "./PatternBuilder.css";

// Text component
const TextComponent = ({ onChange = () => { }, value, onDelete }) => {
    console.log("mounted::", { value, onChange });
    return (
        <div className='pattern-component'>
            <div className="pattern-component-section">
                <div className="label">Free Text</div>
                <Form.Item name="text">
                <Input
                    type='text'
                    className='sequence-input'
                    placeholder="Text"
                    value={value}
                    onChange={(e) => {
                        console.log("e.target.value -> ", e.target.value);
                        onChange(e.target.value)
                    }}
                    width={100}
                />
                </Form.Item>
            </div>
            <Button onClick={onDelete} icon={<CloseOutlined />} className='delete-button' />
        </div>
    );
}

// Sequence component
const SequenceComponent = ({ onChange = () => { }, value, resetsWith, initialValue, onDelete }) => {
    const handleChange = (field, newValue) => {
        onChange({
            sequenceKey: field === 'sequenceKey' ? newValue : value,
            resetsWith: field === 'resetsWith' ? newValue : resetsWith,
            initialValue: field === 'initialValue' ? newValue : initialValue
        });
    };

    return (
        <div className='pattern-component flex'>
            <div className="pattern-component-section">
                <div className="label">Sequence</div>
                <Input
                    className='sequence-input'
                    placeholder="Sequence"
                    value={value}
                    onChange={(e) => handleChange('sequenceKey', e.target.value)}
                    width={100}
                />
            </div>
            <div className="pattern-component-section">
                <div className="label">Reset With</div>
                <Select
                    mode='multiple'
                    className='pattern-component-field'
                    value={resetsWith}
                    onChange={(value) => handleChange('resetsWith', value)}
                    options={[
                        { value: 'level', label: 'level' },
                        { value: 'column', label: 'column' },
                        { value: 'bay', label: 'bay' },
                    ]}
                />
            </div>
            <div className="pattern-component-section initial-value">
                <div className="label">Initial Value</div>
                <Input
                    placeholder="Initial Value"
                    value={initialValue}
                    onChange={(e) => handleChange('initialValue', e.target.value)}
                />
            </div>
            <Button onClick={onDelete} icon={<CloseOutlined />} className='delete-button' />
        </div>
    );
}

// Label component
const LabelComponent = ({ onChange = () => { }, value, onDelete }) => {
    return (
        <div className='pattern-component'>
            <div className="pattern-component-section">
                <div className="label">Label</div>
                <Select
                    className='pattern-component-field'
                    value={value}
                    onChange={(value) => onChange(value)}
                    options={[
                        { value: 'level', label: 'level' },
                        { value: 'column', label: 'column' },
                        { value: 'bay', label: 'bay' },
                    ]}
                />
            </div>
            <Button onClick={onDelete} icon={<CloseOutlined />} className='delete-button' />
        </div>
    );
}

const PatternBuilder = ({ onPatternChange = () => {} }) => {
    const [components, setComponents] = useState([]);
    const [pattern, setPattern] = useState(''); // To store the final pattern

    const availableTokens = [
        { type: 'text', label: 'Text' },
        { type: 'metadata', label: 'Metadata Field' },
        { type: 'sequence', label: 'Sequence' },
    ];

    const addComponent = (type) => {
        const newComponent = {
            id: Date.now(),
            type,
        };
        switch (type) {
            case 'text':
                newComponent.value = '';
                break;
            case 'metadata':
                newComponent.value = '';
                break;
            case 'sequence':
                newComponent.sequenceKey = '';
                newComponent.resetsWith = [];
                newComponent.initialValue = '1';
                break;
            default:
                break;
        }
        setComponents([...components, newComponent]);
    };

    const updateComponent = (index, updatedData) => {
        setComponents((prevComponents) =>
            prevComponents.map((component, i) =>
                i === index
                    ? {
                        ...component,
                        ...(component.type === "sequence"
                            ? {
                                sequenceKey: updatedData.sequenceKey,
                                resetsWith: updatedData.resetsWith,
                                initialValue: updatedData.initialValue,
                            }
                            : { value: updatedData }),
                    }
                    : component
            )
        );
    };


    useEffect(() => {
        console.log("components => ", components);
        const finalPattern = components.map(component => {
            switch (component.type) {
                case 'text':
                    return component.value;
                case 'metadata':
                    return `{${component.value}}`;
                case 'sequence':
                    let patternString = "";
                    if (component.resetsWith.length > 0) {
                        patternString += `${component.resetsWith.join(',')}:`;
                    }
                    if (component.sequenceKey) {                    
                        patternString += `${component.sequenceKey}`;
                    }
                    if (component.initialValue) {
                        patternString += `=${component.initialValue}`;
                    }
                    return `(${patternString})`;
                default:
                    return '';
            }
        }).join('');
        setPattern(finalPattern);
        onPatternChange(finalPattern);
    }, [components]);

    const deleteComponent = (index) => {
        const newComponents = components.filter((_, i) => i !== index);
        setComponents(newComponents);
    };

    const menu = {
        items: availableTokens.map(token => ({
            key: token.type,
            label: token.label,
            onClick: () => addComponent(token.type)
        }))
    };

    const renderComponent = ({ component, index }) => {
        if (component.type === 'text') {
            return (
                <TextComponent
                    key={component.id}
                    value={component.value}
                    onChange={(value) => updateComponent(index, value)}
                    onDelete={() => deleteComponent(index)}
                />
            );
        }
        if (component.type === 'metadata') {
            return (
                <LabelComponent
                    key={component.id}
                    value={component.value}
                    onChange={(value) => updateComponent(index, value)}
                    onDelete={() => deleteComponent(index)}
                />
            );
        }
        if (component.type === 'sequence') {
            return (
                <SequenceComponent
                    key={component.id}
                    value={component.sequenceKey}
                    resetsWith={component.resetsWith}
                    initialValue={component.initialValue}
                    onChange={(data) => updateComponent(index, data)}
                    onDelete={() => deleteComponent(index)}
                />
            );
        }
        return null;
    };

    return (
        <div className="max-w-4xl mx-auto p-4">
            <Card title="Pattern Builder">
                <div className="">
                    <div className="">
                        {components.map((component, index) => (
                            renderComponent({ component, index })
                        ))}
                        <Dropdown menu={menu} trigger={['click']} className='add-component'>
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                className="flex items-center"
                            >
                                Add Component
                            </Button>
                        </Dropdown>
                    </div>
                </div>

                <div className="pattern-preview">
                    <CodeOutlined />
                    <code style={{ marginLeft: 8 }}>
                        {pattern}
                    </code>
                </div>
            </Card>
        </div>
    );
};

export default PatternBuilder;